// ========== React & Libraries ==========
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { ChevronDown } from 'lucide-react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import {
  CoachMissingZoomLicense,
  coachStatusSchema,
  CoachStatus,
  coachSubStatusSchema,
  CoachSubStatus,
  GetCoachesMissingZoomLicenseSortableColumn,
  getCoachesMissingZoomLicenseSortableColumnSchema,
  getCoachesMissingZoomLicenseSortOrderSchema,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { EmptyArray, getCoachesMissingZoomLicense, updateCoachProfile } from '@/lib/api-calls.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { airServerErrorFrom, useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { coachStatusGroupedOptions } from '@/lib/coach-utils.js';
import { airColumnHelper } from '@/lib/tables.js';
import { useSorting } from '@/lib/sorting.js';

// ========== UI Components ==========
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

// ========== Table Components ==========
import { EntityTable, createTableRowSelectionCell } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterDropdownSubItems } from '@/components/table/table-filter-dropdown-sub-items.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';

// ========== Layout Components ==========
import { canCreateCoachUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { InfoPage } from '@/components/info-page.js';

// ========== Users/Teachers Components ==========
import { CoachStatusCell } from '@/routes/_layout/teachers/-components/coach-status-cell.js';
import { CreateCoach } from '@/routes/_layout/teachers/-components/create-coach.js';
import { CreateCoachesByCsv } from '@/routes/_layout/-components/create-coaches-by-csv.js';
import { coachesSectionTabs } from '../-components/tabs.js';
import { formatInstant, FrequentTimeZones, getUpcomingSessions, isAirAdmin } from '@airreading/common';
import { CheckboxZoomLicense } from './-components/checkbox-zoom-license.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';

const usersSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  assignedClass: z.boolean().optional(),
  statuses: z.array(coachStatusSchema).optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  sortBy: getCoachesMissingZoomLicenseSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/teachers/missing-zoom-license/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: UsersIndex,
  validateSearch: usersSearchSchema,
});

const columnHelper = airColumnHelper<CoachMissingZoomLicense>(getCoachesMissingZoomLicenseSortableColumnSchema);

const coachesMissingZoomLicenseTableColumns = [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor('fullNameOrEmail', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to={'/teachers/$teacherId'} params={{ teacherId: row.original.id }}>
        {getValue()}
      </Link>
    ),
  }),

  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),

  columnHelper.accessor((row) => row, {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const { status, subStatuses } = getValue();
      return <CoachStatusCell status={status} subStatuses={subStatuses} />;
    },
  }),

  columnHelper.accessor('studentGroupCount', {
    id: 'studentGroupCount',
    header: 'Number of active classes',
  }),

  columnHelper.accessor('sessions', {
    id: 'nextSession',
    header: 'Next session date',
    cell: ({ getValue }) => {
      const { nextSession } = getUpcomingSessions({
        sessions: getValue() ?? [],
        timeZone: FrequentTimeZones.UsCentral,
      });
      return nextSession && formatInstant(nextSession.startInstant, FrequentTimeZones.UsCentral).formattedDate;
    },
  }),

  columnHelper.accessor('zoomPersonalMeetingUrl', {
    id: 'zoomPersonalMeetingUrl',
    header: 'Zoom link',
  }),

  columnHelper.accessor('zoomAccountId', {
    id: 'zoomAccountId',
    header: 'Zoom ID',
  }),

  columnHelper.accessor((row) => row, {
    id: 'zoomType',
    header: 'License checkbox',
    cell: ({ getValue }) => {
      const coach = getValue();

      return <CheckboxZoomLicense {...coach} />;
    },
  }),
];

function UsersIndex() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      assignedClass: [[assignedClass, activeAssignedClass], setAssignedClass],
      statuses: [[statuses, activeStatuses], setStatuses],
      subStatuses: [[subStatuses, activeSubStatuses], setSubStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'assignedClass', 'statuses', 'subStatuses']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetCoachesMissingZoomLicenseSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);
  const { mutateAsync: updateCoachProfileMutate } = useMutation({ mutationFn: updateCoachProfile });

  const { data, isPending, error, refetch } = useQuery(
    getCoachesMissingZoomLicense({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        ...(activeAssignedClass ? { assignedClass: true } : {}),
        ...(activeStatuses?.length ? { statuses: activeStatuses } : {}),
        ...(activeSubStatuses?.length ? { subStatuses: activeSubStatuses } : {}),
      },
      sortBy,
    }),
  );
  const coaches = useMemo(() => data?.coaches || EmptyArray, [data]);

  useErrorToast(error, 'Error loading teachers');

  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: coaches,
    columns: coachesMissingZoomLicenseTableColumns,
    state: {
      pagination: { pageIndex, pageSize },
      rowSelection,
      sorting,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search name or email"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
      />
      <TableFilterDropdown
        title="Assigned class"
        singleSelect
        options={[{ label: 'Has assigned class', value: true }]}
        selectedValues={assignedClass ? [true] : []}
        onChange={(selected) => {
          setAssignedClass(selected.includes(true) ? true : undefined);
        }}
        data-testid="teachers-missing-zoom-link-filter-dropdown-assigned-class"
      />
      <TableFilterDropdownSubItems<CoachStatus, CoachSubStatus>
        title="Status"
        options={coachStatusGroupedOptions}
        selectedValues={statuses ?? []}
        selectedSubValues={subStatuses ?? []}
        onChange={(selected) => setStatuses(selected.length > 0 ? selected : undefined)}
        onSubValuesChange={(selected) => setSubStatuses(selected.length > 0 ? selected : undefined)}
        data-testid="teachers-missing-zoom-link-filter-dropdown-status"
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
    </>
  );

  const onBulkCheckZoomLicense = async () => {
    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);

    try {
      const promises = selectedRows.map(
        async ({
          id,
          JPSGoogleMeetUrl,
          zoomPersonalMeetingUrl,
          airReadingGoogleMeetUrl,
          zoomPersonalMeetingId,
          zoomType,
          zoomTechCheck,
          googleMeetTechCheck,
          classEligibilities,
          backgroundCheckStatuses,
        }) =>
          await updateCoachProfileMutate({
            userId: id,
            data: {
              zoomPersonalMeetingUrl: zoomPersonalMeetingUrl ?? undefined,
              zoomPersonalMeetingId: zoomPersonalMeetingId ?? undefined,
              zoomType: zoomType ?? undefined,
              JPSGoogleMeetUrl: JPSGoogleMeetUrl ?? undefined,
              airReadingGoogleMeetUrl: airReadingGoogleMeetUrl ?? undefined,
              zoomTechCheck: zoomTechCheck ?? undefined,
              googleMeetTechCheck: googleMeetTechCheck ?? undefined,
              backgroundCheckStatuses: backgroundCheckStatuses ?? [],
              classEligibilities: classEligibilities ?? [],
            },
          }),
      );

      await Promise.all(promises);

      queryClient.invalidateQueries(getCoachesMissingZoomLicense());
    } catch (e) {
      toast({
        title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to update zoom license',
      });
    }
  };

  const bulkActions = <DropdownMenuItem onClick={onBulkCheckZoomLicense}>Check Zoom license</DropdownMenuItem>;

  const createUsersMenu = canCreateCoachUsers(actingUser) && (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add teachers
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateCoach
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a teacher</DropdownMenuItem>}
        />
        <CreateCoachesByCsv
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        autoManagedColumnIds={['selection']}
        hasError={!!error}
        emptyState="No users found"
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
        bulkActionsMenuItems={bulkActions}
        topBarRightComponents={createUsersMenu}
      />
    </InfoPage>
  );
}
